import React, { useState, useCallback, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { MONOBLOCK_STYLES } from "../data/monoblockStyles";
import MediaLightbox from "../components/MediaLightbox";
import Seo from "../components/Seo";

/* ---------- Per-wheel CTA copy (edit anytime) ---------- */
const CTA_BY_SLUG = {
  m1: "The M1 features a bold Y-split 5-spoke layout with lightweight cutouts engineered for maximum performance and visual punch. Its sharp geometry and deep machining make any build look wider, lower, and more aggressive. See how our clients fitted this wheel on their builds below.",
  m2: "The M2’s directional Aero-Lip and 10-spoke cut center capture movement even at a standstill. Designed for balance and aerodynamic efficiency, it delivers a modern motorsport presence with timeless symmetry. Explore on-car and studio samples below.",
  m3: "The M3 showcases a deep concave face and muscular spoke curvature that radiates strength. Ideal for wide-stance builds, it merges luxury detailing with track-ready aggression. Browse real builds below.",
  m4: "The M4 refines the art of precision with a thin, multi-spoke layout that emphasizes sophistication and performance. Its lightweight structure provides both aesthetic depth and structural integrity under pressure. See how it looks on-car below.",
  m5: "The M5’s sculpted split-5 design blends style and strength through aggressive machining and precision cutouts. It’s built for enthusiasts who demand maximum detail and dynamic energy in every rotation. View client installs and studio shots below.",
  m6: "The M6 delivers a clean, motorsport-inspired look with sharp lines and open-air design for brake-cooling efficiency. Lightweight yet bold, it’s purpose-built for performance and presence. Check the gallery below.",
  m7: "The M7 stands out with its layered, complex spoke geometry that creates visual depth and motion from every angle. Forged for strength and balance, it gives high-end builds an unmistakably technical aesthetic. See it on and off the car below.",
  m8: "The M8, known as the “China Plate,” is a bold full-face forged design that radiates luxury and dominance. Its precision-machined vent windows and smooth, sculpted surface make it the ultimate statement piece for high-end builds that demand attention. Explore real customer builds below.",
  m9: "The M9 combines a concave face with smooth transitions and beveled edges for a premium, high-performance look. Designed to fill the wheel well with presence, it defines modern luxury through forged craftsmanship. Browse samples below.",
};
function ctaFor(slug, styleName) {
  return CTA_BY_SLUG[slug] || `${styleName} — Custom-forged monoblock built to your exact specs. See our samples below.`;
}

/* ---------- CRA/Webpack gallery loader (per slug, tolerant of missing folders) ---------- */
function importAll(ctx) {
  return ctx
    .keys()
    .sort() // 01, 02, 03 ... 10 (use leading zeros for order)
    .map((key) => {
      const file = key.replace("./", "");
      const isVideo = /\.(mp4|mov|webm)$/i.test(file);
      return { type: isVideo ? "video" : "image", src: ctx(key) };
    });
}

const SAMPLE_CONTEXTS = {};
try { SAMPLE_CONTEXTS.m1 = require.context("../assets/samples/m1", false, /\.(png|jpe?g|webp|mp4|mov|webm)$/i); } catch {}
try { SAMPLE_CONTEXTS.m2 = require.context("../assets/samples/m2", false, /\.(png|jpe?g|webp|mp4|mov|webm)$/i); } catch {}
try { SAMPLE_CONTEXTS.m3 = require.context("../assets/samples/m3", false, /\.(png|jpe?g|webp|mp4|mov|webm)$/i); } catch {}
try { SAMPLE_CONTEXTS.m4 = require.context("../assets/samples/m4", false, /\.(png|jpe?g|webp|mp4|mov|webm)$/i); } catch {}
try { SAMPLE_CONTEXTS.m5 = require.context("../assets/samples/m5", false, /\.(png|jpe?g|webp|mp4|mov|webm)$/i); } catch {}
try { SAMPLE_CONTEXTS.m6 = require.context("../assets/samples/m6", false, /\.(png|jpe?g|webp|mp4|mov|webm)$/i); } catch {}
try { SAMPLE_CONTEXTS.m7 = require.context("../assets/samples/m7", false, /\.(png|jpe?g|webp|mp4|mov|webm)$/i); } catch {}
try { SAMPLE_CONTEXTS.m8 = require.context("../assets/samples/m8", false, /\.(png|jpe?g|webp|mp4|mov|webm)$/i); } catch {}
try { SAMPLE_CONTEXTS.m9 = require.context("../assets/samples/m9", false, /\.(png|jpe?g|webp|mp4|mov|webm)$/i); } catch {}

export default function MonoblockStyleDetail() {
  const { styleSlug } = useParams();
  const style = MONOBLOCK_STYLES.find((s) => s.slug === styleSlug);

  const samples = useMemo(() => {
    const ctx = SAMPLE_CONTEXTS[styleSlug];
    return ctx ? importAll(ctx) : [];
  }, [styleSlug]);

  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);

  const openAt = useCallback((i) => { setActiveIndex(i); setLightboxOpen(true); }, []);
  const close  = useCallback(() => setLightboxOpen(false), []);
  const prev   = useCallback(() => setActiveIndex((i) => (i - 1 + samples.length) % samples.length), [samples.length]);
  const next   = useCallback(() => setActiveIndex((i) => (i + 1) % samples.length), [samples.length]);

  if (!style) {
    return (
      <div className="max-w-5xl mx-auto p-6 text-neutral-900">
        <p className="opacity-80">Style not found.</p>
        <Link to="/wheels/forged-monoblock" className="underline">Back to Monoblock</Link>
      </div>
    );
  }

  const CTA_TEXT = ctaFor(styleSlug, style.name);


// ---- SEO: title, description, canonical, social image, JSON-LD ----
const pageTitle = `Unlimited Levels ${style?.name || styleSlug.toUpperCase()} | Forged Monoblock Wheel`;
const pageDesc =
  (style?.seoDescription || style?.blurb || CTA_TEXT || "Unlimited Levels forged monoblock wheels built without limits.")
    .replace(/\s+/g, " ")
    .trim();
const pageUrl = `https://unlimitedlevels.co/wheels/forged-monoblock/${styleSlug}`;
const ogImage = "https://unlimitedlevels.co/assets/og/unlimited-levels-hero-1200x630.jpg"; // 1200×630 promo

const productLd = {
  "@context": "https://schema.org",
  "@type": "Product",
  "name": `Unlimited Levels ${style?.name || styleSlug.toUpperCase()} Forged Monoblock Wheel`,
  "brand": { "@type": "Brand", "name": "Unlimited Levels" },
  "image": [ogImage],
  "description": pageDesc,
  "category": "AutomotivePart",
  "url": pageUrl,
  "offers": {
    "@type": "Offer",
    "price": "0.00",
    "priceCurrency": "USD",
    "availability": "https://schema.org/InStock"
  }
};



  return (
 <>
    <Seo
      title={pageTitle}
      description={pageDesc}
     canonical={pageUrl}
      ogImage={ogImage}
      ogUrl={pageUrl}
      jsonLd={productLd}
    />

    <div className="max-w-6xl mx-auto p-6 text-neutral-900">
      <Link to="/wheels/forged-monoblock" className="text-sm underline">← Back to Monoblock</Link>

      <div className="grid md:grid-cols-2 gap-8 mt-6">
        {/* Left: big product image */}
        <div className="bg-neutral-100 rounded-2xl overflow-hidden">
          <img src={style.img} alt={style.name} className="w-full h-auto" />
        </div>

        {/* Right: title, blurb, CTA text + Request button */}
        <div>
          <h1 className="text-3xl font-bold">{style.name}</h1>
          <p className="mt-2 text-neutral-700">{style.blurb}</p>

          {/* FIXED <ul> — every <li> is closed properly */}
          <ul className="mt-6 space-y-2 text-sm text-neutral-700">
            <li>• Aerospace-grade 6061-T6 monoblock forging CNC</li>
            <li>• 17" - 30" diameters, custom widths and offsets</li>
            <li>• Brake-clearance engineered per vehicle</li>
            <li>• Every Unlimited Levels wheel is forged to perform for life. Each design is precision-engineered,           		  
                  tested, and finished      to meet the highest standards in the industry.</li>
            <li>• Finishes: Painted, Brushed, Polished, Tinted Clearcoat, Clearcoat over Raw</li>
            <li>• Lifetime Structural Warranty*</li>
            <li>• Average production time is 3-4 weeks</li>
          </ul>

          <p className="mt-6 text-base md:text-lg text-neutral-900">{CTA_TEXT}</p>

          <Link
            to="/quote"
            className="inline-flex items-center justify-center mt-4 px-6 py-3 rounded-xl bg-black text-white font-semibold shadow-lg hover:bg-neutral-800 active:translate-y-px transition"
          >
            Request a Quote
          </Link>
        </div>
      </div>

      {/* Samples / Lookbook */}
      {samples.length > 0 ? (
        <>
          <h3 className="mt-12 mb-3 text-xl font-semibold tracking-tight">Lookbook — real builds & samples</h3>
          <p className="mb-6 text-sm text-neutral-600">
            Click any image or video to view full screen. Use ◀ ▶ keys, or Esc to close.
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {samples.map((m, i) => (
              <button
                key={m.src}
                onClick={() => openAt(i)}
                className="group relative overflow-hidden rounded-xl bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-neutral-900"
                aria-label="Open sample"
              >
                {m.type === "image" ? (
                  <img
                    src={m.src}
                    alt=""
                    className="h-36 w-full object-cover group-hover:scale-105 transition-transform"
                    loading="lazy"
                  />
                ) : (
                  <div className="h-36 w-full overflow-hidden">
                    <video
                      src={m.src}
                      className="h-full w-full object-cover"
                      muted
                      loop
                      playsInline
                      autoPlay
                    />
                    <span className="absolute bottom-2 right-2 text-[11px] bg-black/60 text-white px-2 py-0.5 rounded">
                      video
                    </span>
                  </div>
                )}
              </button>
            ))}
          </div>

          {lightboxOpen && (
            <MediaLightbox
              items={samples}
              index={activeIndex}
              onClose={close}
              onPrev={prev}
              onNext={next}
            />
          )}
        </>
      ) : (
        <p className="mt-12 text-sm text-neutral-500">
          No samples for <span className="font-semibold">{styleSlug}</span> yet.
          Add files to <code className="mx-1">src/assets/samples/{styleSlug}/</code> and restart the dev server.
        </p>
      )}
    </div>
    </>
  );
}
